import { Component, Inject, OnInit } from '@angular/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { InAppNotificationPreference } from 'src/app/in-app-notifications/models/in-app-notification-preference.model';
import { InAppNotificationType } from 'src/app/in-app-notifications/models/in-app-notification-type.enum';
import {
    getInAppNotificationPreferences,
    updateInAppNotificationPreference
} from 'src/app/in-app-notifications/store/actions/in-app-notification-preferences.actions';
import { InAppNotificationPreferencesState } from 'src/app/in-app-notifications/store/in-app-notification-preferences.state';
import {
    selectLoading,
    selectInAppNotificationPreferences
} from 'src/app/in-app-notifications/store/selectors/in-app-notification-preferences.selectors';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';

@UntilDestroy()
@Component({
    selector: 'nova-in-app-notifications-preferences',
    standalone: true,
    imports: [
        MatProgressBarModule,
        MatButtonModule,
        MatIconModule,
        MatExpansionModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatDialogModule,
        AsyncPipe,
        NgTemplateOutlet
    ],
    templateUrl: './in-app-notifications-preferences.component.html',
    styleUrls: ['./in-app-notifications-preferences.component.scss']
})
export class InAppNotificationsPreferencesComponent implements OnInit {
    private readonly assignedToMeTypes = new Set();
    private readonly createdByMeTypes = new Set();
    featureFlag = FeatureFlag;
    isLoading$: Observable<boolean>;
    preferences: { [key: string]: InAppNotificationPreference[] } = {};
    tasksOpen = false;
    ticketsOpen = false;
    jobsOpen = false;
    enableInAppNotificationsTickets = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) private readonly data: any,
        private readonly _store: Store<InAppNotificationPreferencesState>,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {}

    ngOnInit(): void {
        this.isLoading$ = this._store.select(selectLoading);

        this._store.dispatch(getInAppNotificationPreferences());

        this.initPanel();

        combineLatest([
            this._ldFeatureManager.isEnabled(FeatureFlag.InAppNotificationsTickets),
            this._store.select(selectInAppNotificationPreferences)
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([isTicketsEnabled, preferences]) => {
                this.assignedToMeTypes.add(InAppNotificationType.TaskAssignedToMeEdited);
                this.assignedToMeTypes.add(InAppNotificationType.TaskAssignedToMeInProgress);
                this.assignedToMeTypes.add(InAppNotificationType.TaskAssignedToMeCompleted);
                this.createdByMeTypes.add(InAppNotificationType.TaskCreatedByMeEdited);
                this.createdByMeTypes.add(InAppNotificationType.TaskCreatedByMeInProgress);
                this.createdByMeTypes.add(InAppNotificationType.TaskCreatedByMeCompleted);
                this.createdByMeTypes.add(InAppNotificationType.TaskCreatedByMeOverdue);
                if (isTicketsEnabled) {
                    this.enableInAppNotificationsTickets = true;
                }

                this.assignedToMeTypes.add(InAppNotificationType.JobsActionCompleted);
                this.assignedToMeTypes.add(InAppNotificationType.JobsActionSkipped);
                this.createdByMeTypes.add(InAppNotificationType.JobsCustomActionCompleted);
                this.createdByMeTypes.add(InAppNotificationType.JobsCustomActionSkipped);
                this.initPreferences(preferences);
            });
    }

    isJobsType(type: InAppNotificationType) {
        switch (type) {
            case InAppNotificationType.JobsActionCompleted:
            case InAppNotificationType.JobsActionSkipped:
            case InAppNotificationType.JobsCustomActionCompleted:
            case InAppNotificationType.JobsCustomActionSkipped:
                return true;
            default:
                return false;
        }
    }

    initPanel(): void {
        if (this.data && this.data?.currentTab) {
            this.onPanelOpened(this.data?.currentTab);
        }
    }

    initPreferences(preferencesIn: InAppNotificationPreference[]): void {
        this.preferences.TasksAssignedToMe = [];
        this.preferences.ActionsAssignedToMe = [];
        this.assignedToMeTypes.forEach((typeId) => {
            const match = preferencesIn.find((t) => t.inAppNotificationTypeId === typeId);
            if (match !== undefined) {
                if (this.isJobsType(typeId as InAppNotificationType)) {
                    this.preferences.ActionsAssignedToMe.push(match);
                } else {
                    this.preferences.TasksAssignedToMe.push(match);
                }
            }
        });
        this.preferences.TasksCreatedByMe = [];
        this.preferences.ActionsCreatedByMe = [];
        this.createdByMeTypes.forEach((typeId) => {
            const match = preferencesIn.find((t) => t.inAppNotificationTypeId === typeId);
            if (match !== undefined) {
                if (this.isJobsType(typeId as InAppNotificationType)) {
                    this.preferences.ActionsCreatedByMe.push(match);
                } else {
                    this.preferences.TasksCreatedByMe.push(match);
                }
            }
        });
    }

    onPanelOpened(panel: string) {
        const panelName = panel.split(' ', 2);
        switch (panelName[0].toLocaleLowerCase()) {
            case 'tasks':
                this.tasksOpen = true;
                this.ticketsOpen = false;
                this.jobsOpen = false;
                break;
            case 'tickets':
                this.tasksOpen = false;
                this.ticketsOpen = true;
                this.jobsOpen = false;
                break;
            case 'jobs':
                this.tasksOpen = false;
                this.ticketsOpen = false;
                this.jobsOpen = true;
                break;
            default:
                this.tasksOpen = false;
                this.ticketsOpen = false;
                this.jobsOpen = false;
                break;
        }
    }

    togglePreferenceEnabled(preference: InAppNotificationPreference) {
        this._store.dispatch(
            updateInAppNotificationPreference({
                preferenceId: preference.id,
                isEnabled: !preference.isEnabled
            })
        );
    }
}
